import { BrowserRouter as Router, Routes, Route, Link, useParams, useLocation, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IntroPage } from "./components/IntroPage";
import { NavbarWrapper } from "./components/NavbarWrapper";
import { SchedulePageWrapper } from "./components/schedule/SchedulePageWrapper";
import { ExpoPageWrapper } from "./components/wrappers/ExpoPageWrapper";
import { SpeakersPage } from "./components/speakers/SpeakersPage";
import { DetailsPageWrapper } from "./components/details/DetailsPageWrapper";
import { CustomPageWrapper } from "./components/customPages/CustomPageWrapper";
import { PublicSurveyWrapper } from "./components/surveys/PublicSurveyWrapper";
import { FormsPageWrapper } from "./components/wrappers/FormsPageWrapper";
import { SurveysPageWrapper } from "./components/wrappers/SurveysPageWrapper";
import { EventFormWrapper } from "./components/forms/EventFormWrapper";
import { EventProvider } from "./contexts/EventContext";
import { SocialMediaFooter } from "./components/SocialMediaFooter";
import { RouteGuard } from "./components/RouteGuard";
import { EventAvailabilityGuard } from "./components/EventAvailabilityGuard";
import { useEventDetails } from "../hooks/useEventDetails";
import { useEffect } from 'react';
import { initializeGTM, trackPageView, trackNavigation, setUserData, setTrackingParams, UserData } from './utils/gtm';
import { urlTrackingManager } from '../utils/urlTracking';
import { CustomCSSProvider } from './components/CustomCSSProvider';
import { BackgroundStyleWrapper } from './components/BackgroundStyleWrapper';

interface AppProps {
  style?: {
    id: number;
    backgroundColor?: string;
    backgroundUrl?: string;
    backgroundPosition?: string;
    backgroundTiled?: boolean;
    headerColor?: string;
    textColor?: string;
    customFont?: string;
  };
  customCss?: string;
  analytics?: {
    gtmId?: string;
    googleAnalyticsId?: string;
  };
  currentUser?: UserData;
}

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Helper to get event slug from the URL
const GetEventSlug = () => {
  // Extract the event slug from the pathname
  // The URL pattern is /:event_slug or /:event_slug/schedule, etc.
  const pathSegments = window.location.pathname.split('/').filter(Boolean);

  // First segment should be the event slug
  const eventSlug = pathSegments[0];

  // Filter out known route segments to get just the event slug
  const knownRoutes = ['details', 'schedule', 'speakers', 'expo', 'registration', 'resources', 'forum', 'faq', 'docs', 'pages', 'surveys', 'forms'];

  if (eventSlug && !knownRoutes.includes(eventSlug)) {
    return eventSlug;
  }

  // Fallback - this shouldn't happen in normal usage
  return 'event';
}

// Component that renders the app content with footer
function AppContent({ style, customCss, analytics, currentUser }: { style?: AppProps['style']; customCss?: string; analytics?: AppProps['analytics']; currentUser?: UserData }) {
  const eventSlug = GetEventSlug();
  const { data: eventDetails } = useEventDetails(eventSlug);
  const location = useLocation();

  // Set user data when available
  useEffect(() => {
    if (currentUser) {
      setUserData(currentUser);
    }
  }, [currentUser]);

  // Set tracking parameters from URL
  useEffect(() => {
    const trackingParams = urlTrackingManager.getTrackingParams();
    if (Object.keys(trackingParams).length > 0) {
      setTrackingParams(trackingParams);
    }
  }, []);

  // Track page views on route changes
  useEffect(() => {
    if (analytics?.gtmId) {
      trackPageView({
        page_path: location.pathname,
        event_slug: eventSlug,
        event_name: eventDetails?.name,
        user_type: currentUser?.isActive ? 'registered' : 'guest'
      });

      // Also track navigation events
      trackNavigation(location.pathname, {
        event_slug: eventSlug,
        event_name: eventDetails?.name
      });
    }
  }, [location.pathname, analytics?.gtmId, eventSlug, eventDetails?.name, currentUser]);

  return (
    <div className={`min-h-screen flex flex-col ${!style ? 'bg-gray-50' : ''}`}>
      <NavbarWrapper />
      <div className="pt-16 flex-grow"> {/* Add padding-top to account for fixed navbar height and flex-grow to push footer down */}
        <div className="max-w-7xl mx-auto">
          <RouteGuard eventSlug={eventSlug}>
            <Routes>
            {/* Routes are relative to the basename (/:event_slug) */}
            <Route path="/" element={<DetailsPageWrapper />} />
            <Route path="/details" element={<DetailsPageWrapper />} />
          {/* Update the registration route element */}
          <Route
            path="/registration"
            element={
              <div className="text-center p-10">
                <h1 className="text-3xl font-bold mb-4">Event Registration</h1>
                <p className="mb-6">Click the button below to start your registration.</p>
                {/* Link to the new registration portal path */}
                <Link
                  to={`/${eventSlug}/register`} // Navigate outside the current Router scope
                  onClick={(e) => {
                    e.preventDefault(); // Prevent Router navigation
                    window.location.href = `/${eventSlug}/register`; // Force full page navigation
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                >
                  Register Now
                </Link>
              </div>
            }
          />
          <Route path="/schedule" element={<SchedulePageWrapper />} />
          <Route path="/agenda" element={<Navigate to="/schedule" replace />} /> {/* Redirect legacy /agenda URLs to /schedule */}
          <Route path="/speakers" element={<SpeakersPage />} />
          <Route path="/expo" element={<ExpoPageWrapper />} />
          <Route path="/pages/:pageSlug" element={<CustomPageWrapper />} />
          <Route path="/surveys" element={<SurveysPageWrapper />} />
          <Route path="/surveys/:surveySlug" element={<PublicSurveyWrapper />} />
          <Route path="/days/:daySlug/sessions/:sessionSlug/surveys/:surveySlug" element={<PublicSurveyWrapper />} />
          <Route path="/forms" element={<FormsPageWrapper />} />
          <Route path="/forms/:form_slug" element={<EventFormWrapper />} />
        </Routes>
          </RouteGuard>
        </div>
      </div>
      {/* Social Media Footer - outside max-w-7xl so it spans full width */}
      {eventDetails && (
        <SocialMediaFooter
          socialMediaLinks={eventDetails.socialMediaLinks || []}
          eventName={eventDetails.name}
          style={eventDetails.style}
        />
      )}
    </div>
  );
}

export function App({ style, customCss, analytics, currentUser }: AppProps) {
  const eventSlug = GetEventSlug(); // Get the event slug
  const basename = `/${eventSlug}`;

  // Initialize GTM on app startup
  useEffect(() => {
    if (analytics?.gtmId) {
      initializeGTM({
        gtmId: analytics.gtmId,
        googleAnalyticsId: analytics.googleAnalyticsId
      });
    }
  }, [analytics?.gtmId, analytics?.googleAnalyticsId]);


  return (
    <QueryClientProvider client={queryClient}>
      <CustomCSSProvider customCss={customCss} style={style} />
      <EventProvider eventSlug={eventSlug} style={style}>
        <BackgroundStyleWrapper eventSlug={eventSlug} style={style}>
          <EventAvailabilityGuard eventSlug={eventSlug}>
            <Router basename={basename}>
              <AppContent style={style} customCss={customCss} analytics={analytics} currentUser={currentUser} />
            </Router>
          </EventAvailabilityGuard>
        </BackgroundStyleWrapper>
      </EventProvider>
    </QueryClientProvider>
  );
}
